import { formatInTimeZone } from 'date-fns-tz';
import { slugify } from '../excel/exportClosure';
import {
  banderasDeCanje, registrosVigentes, totalesGenerales, unidadesDePremio,
} from '../canjesTotales';
import type { ReporteMeta } from '../excel/exportReportes';
import type { CanjeRow } from '../../api/canjes';

/**
 * Se re-exportan sin cambiarlas porque su definición ya NO vive acá: se mudó a
 * `domain/canjesTotales`, que es el único sitio donde se cuentan canjes. Este archivo las tenía
 * copiadas y esa copia es justamente el error que se cerró —la pantalla, el Excel y este HTML
 * daban tres cifras distintas de "canjes" porque cada uno las sumaba por su cuenta—.
 */
export { registrosVigentes, totalesGenerales, unidadesDePremio };

const TZ = 'America/Lima';

/**
 * Las fotos ya bajadas y convertidas, por su ruta del Storage. El valor es un `data:` URI
 * completo. Una ruta que no está en el mapa es una foto que no se pudo bajar.
 */
export type FotosEmbebidas = Map<string, string>;

/**
 * Escapa texto para incrustarlo en HTML.
 *
 * **No es cosmético.** El nombre del cliente, las observaciones y el número de boleta los teclea
 * el campo desde un celular, y este archivo se abre con doble clic FUERA de la aplicación, sin
 * React de por medio —que escapa solo—. Un `<script>` escrito en "observaciones" se ejecutaría
 * en la máquina de quien abra el reporte.
 *
 * Desde el rediseño el archivo SÍ lleva su propio script (filtros y lightbox), así que esto pasó
 * de importante a crítico: ver `serializarDatos` para el escapado del bloque JSON, que es un
 * contexto distinto y necesita otras reglas.
 */
export function escaparHtml(v: unknown): string {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Las rutas de foto de un registro, sin nulos ni vacías. */
export function rutasDeFoto(r: CanjeRow): string[] {
  return [r.fotoBoletaPath, r.fotoPremioPath].filter(
    (p): p is string => p !== null && p.trim() !== '',
  );
}

/** Todas las rutas de foto de un reporte, sin repetir: es lo que hay que bajar. */
export function rutasDeFotoDeTodos(rows: CanjeRow[]): string[] {
  return [...new Set(rows.flatMap(rutasDeFoto))];
}

/* ------------------------------------------------------------------ *
 * Formato
 * ------------------------------------------------------------------ */

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * `2026-08-15` → `15 ago 2026`.
 *
 * Se parte la cadena a mano en vez de construir un `Date`: `fecha` ya viene como 'yyyy-MM-dd' en
 * hora de Lima, y `new Date('2026-08-15')` lo interpreta como UTC, así que en el navegador de
 * quien abre el archivo —que puede estar en otra zona— el día se corre uno hacia atrás. Ese error
 * no se ve en la máquina de quien genera el reporte, solo en la de quien lo recibe.
 */
export function fechaCorta(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (m === null) return iso;
  const mes = MESES[Number(m[2]) - 1] ?? m[2];
  return `${Number(m[3])} ${mes} ${m[1]}`;
}

/** Singular o plural, explícito. El diseño lo pide en día, premio, registro y unidad. */
function plural(n: number, singular: string, muchos: string): string {
  return `${n} ${n === 1 ? singular : muchos}`;
}

/**
 * Soles con dos decimales, en el mismo formato en todo el archivo: un importe escrito de dos
 * maneras en el mismo documento se lee como si fueran dos cifras distintas.
 */
function soles(n: number): string {
  return `S/ ${n.toFixed(2)}`;
}

/**
 * Ordena por total descendente y usa el nombre SOLO para desempatar.
 *
 * El desempate no es estético: sin él, dos tiendas con el mismo total quedan en el orden en que
 * las devolvió la consulta —que no está garantizado y cambia entre descargas— y el mismo dato
 * bajado dos veces se ve como si los números hubieran cambiado. Quien recibe el reporte no tiene
 * cómo distinguir "se reordenó" de "se movió algo".
 */
function ordenar<T>(filas: T[], total: (f: T) => number, nombre: (f: T) => string): T[] {
  return filas.sort((a, b) => total(b) - total(a) || nombre(a).localeCompare(nombre(b), 'es'));
}

/** Etiqueta de agrupación: lo vacío se nombra, porque una fila sin rótulo no se puede leer. */
function rotulo(v: string | null | undefined, siFalta: string): string {
  const limpio = (v ?? '').trim();
  return limpio === '' ? siFalta : limpio;
}

/**
 * Rótulo de una fila que se agrupa por ID: el código delante del nombre.
 *
 * **El nombre no identifica.** `pdv.name` no tiene índice único —lo único único es `pdv.code`— y
 * `campaign_dynamics` es `unique (campaign_id, code)`, tampoco por nombre. Las cadenas repiten el
 * nombre del local entre distritos, así que agrupar por nombre fundía dos tiendas distintas en una
 * sola fila y quien recibía el reporte no tenía cómo notarlo: en una liquidación por tienda eso es
 * plata atribuida a la tienda equivocada.
 */
function rotuloConCodigo(
  code: string | null | undefined, name: string | null | undefined, siFalta: string,
): string {
  const c = (code ?? '').trim();
  const n = (name ?? '').trim();
  if (c === '') return n === '' ? siFalta : n;
  return n === '' ? c : `${c} · ${n}`;
}


/* ------------------------------------------------------------------ *
 * Derivados de UN canje
 *
 * El contrato del diseño asume un premio plano y una dinámica por canje. El sistema los tiene un
 * nivel más abajo (`lineas[].premios[]`) y admite varias de cada uno. Aplanar sin agregar sería
 * perder dato, así que se agrega acá y se deja constancia de por qué.
 * ------------------------------------------------------------------ */

export interface PremioDeCanje {
  nombre: string;
  unidades: number;
  /** Alguna de las líneas que entregó este premio lo hizo por equivalencia. */
  equivalencia: boolean;
}

/**
 * Los premios de un canje, agregados POR NOMBRE.
 *
 * Un mismo premio puede venir en dos líneas del mismo canje (caso previsto: dos dinámicas que
 * entregan la misma batea). Aplanar sin agregar pintaría dos chips «Batea ×1» en la misma tarjeta
 * en vez de uno «Batea ×2», y quien audita contaría dos entregas donde hubo una de dos unidades.
 *
 * Orden alfabético y no por unidades: dentro de una tarjeta el orden por cantidad haría que el
 * mismo canje se lea distinto según qué premio subió, y no hay nada que ranquear en dos chips.
 */
export function premiosDelCanje(r: CanjeRow): PremioDeCanje[] {
  const acc = new Map<string, PremioDeCanje>();
  for (const l of r.lineas) {
    for (const p of l.premios) {
      const nombre = rotulo(p.prizeName, 'Sin nombre');
      const f = acc.get(nombre) ?? { nombre, unidades: 0, equivalencia: false };
      f.unidades += p.units;
      f.equivalencia = f.equivalencia || p.equivalencia;
      acc.set(nombre, f);
    }
  }
  return [...acc.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

export interface DinamicaDeCanje {
  id: string;
  rotulo: string;
  canjes: number;
}

/**
 * Las dinámicas de un canje, agregadas por id.
 *
 * El diseño pide UNA dinámica por tarjeta. El sistema admite varias —`listCanjesBandeja` filtra
 * dinámica en memoria justamente porque un canje puede tener líneas de dos— así que la tarjeta
 * pinta un chip por dinámica en vez de elegir una y esconder la otra. Nunca se imprime
 * «Multi-dinámica» dentro de una tarjeta: eso oculta cuál fue.
 */
export function dinamicasDelCanje(r: CanjeRow): DinamicaDeCanje[] {
  const acc = new Map<string, DinamicaDeCanje>();
  for (const l of r.lineas) {
    const f = acc.get(l.dynamicId)
      ?? { id: l.dynamicId, rotulo: rotuloConCodigo(l.dynamicCode, l.dynamicName, 'Sin dinámica'), canjes: 0 };
    f.canjes += l.canjes;
    acc.set(l.dynamicId, f);
  }
  return [...acc.values()].sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'es'));
}

/** Nombre de tienda con su código, para la rejilla de campos y las tablas. */
function tiendaDe(r: CanjeRow): string {
  return rotuloConCodigo(r.pdv?.code, r.pdv?.name, 'Sin tienda');
}

/**
 * Si el reporte debe mostrar cifras de monto.
 *
 * Tres estados, no dos, y la diferencia importa:
 * - alguna dinámica dispara por `'monto'` → SÍ: la campaña pide el monto, así que un registro sin
 *   monto es un hueco que hay que ver.
 * - ninguna lo pide → NO: no hay nada que mostrar y el reporte lo explica en una nota.
 * - hay montos anotados aunque ninguna dinámica lo pida (dato viejo, dinámica cambiada después)
 *   → SÍ, por el `||`: un monto que EXISTE no se esconde nunca. Esconderlo sería borrar dato de la
 *   vista del cliente sin decírselo.
 */
export function capturaMonto(rows: CanjeRow[]): boolean {
  const vigentes = registrosVigentes(rows);
  return vigentes.some((r) => r.lineas.some((l) => l.triggerKind === 'monto'))
    || vigentes.some((r) => r.montoCompra !== null);
}

/* ------------------------------------------------------------------ *
 * Agregados del reporte
 *
 * Todos sobre `registrosVigentes`: anular es la forma de eliminar y el anulado sale de los
 * conteos, aunque siga visible en su tarjeta.
 * ------------------------------------------------------------------ */

export interface FilaEntrega {
  /** `T10026 · Makro Venezuela`. */
  tienda: string;
  /** ISO. Es el que ordena — nunca el texto formateado. */
  fechaIso: string;
  premio: string;
  unidades: number;
  /** Registros de esta combinación. NO se suman los canjes del canje entero: ver abajo. */
  registros: number;
  canjes: number;
}

export interface EntregasLargas {
  filas: FilaEntrega[];
  totalUnidades: number;
  totalCanjes: number;
  dias: number;
  premios: number;
}

/**
 * Entregas en formato largo: una fila por tienda × fecha × premio.
 *
 * Sustituye a la matriz de encabezado de dos niveles que tenía el reporte anterior. La matriz
 * generaba una columna por fecha, así que con un mes de campaña y 19 tiendas producía una tabla
 * de 60 columnas con scroll horizontal ilegible; el formato largo escala a cualquier número de
 * tiendas, fechas y premios sin columnas dinámicas.
 *
 * **Los canjes NO se suman por línea de premio.** El prototipo del diseño hace
 * `fila.canjes += canje.canjes` una vez por cada premio, así que un canje con dos premios suma sus
 * canjes en las DOS filas y la columna Canjes termina dando más que el total del reporte. Acá se
 * cuenta el canje una sola vez por combinación, con el mismo criterio que `resumenPorPremio` usa
 * para "en cuántos registros aparece". Las UNIDADES sí se suman siempre: las dos se entregaron.
 */
export function entregasPorTiendaFechaPremio(rows: CanjeRow[]): EntregasLargas {
  const acc = new Map<string, FilaEntrega>();
  const vistosPorClave = new Map<string, Set<string>>();
  const fechas = new Set<string>();
  const premios = new Set<string>();

  for (const r of registrosVigentes(rows)) {
    fechas.add(r.fecha);
    // Se agrupa por id de tienda y no por nombre —dos tiendas pueden llamarse igual— pero la
    // clave lleva también el rótulo para que el orden alfabético del rótulo sea estable.
    const idTienda = r.pdv?.id ?? '';
    const tienda = tiendaDe(r);
    for (const p of premiosDelCanje(r)) {
      premios.add(p.nombre);
      const clave = `${idTienda}|${r.fecha}|${p.nombre}`;
      const f = acc.get(clave)
        ?? { tienda, fechaIso: r.fecha, premio: p.nombre, unidades: 0, registros: 0, canjes: 0 };
      f.unidades += p.unidades;
      const vistos = vistosPorClave.get(clave) ?? new Set<string>();
      if (!vistos.has(r.id)) {
        f.registros += 1;
        f.canjes += r.totalCanjes;
        vistos.add(r.id);
        vistosPorClave.set(clave, vistos);
      }
      acc.set(clave, f);
    }
  }

  const filas = [...acc.values()].sort((a, b) => a.tienda.localeCompare(b.tienda, 'es')
    || a.fechaIso.localeCompare(b.fechaIso)
    || a.premio.localeCompare(b.premio, 'es'));

  const t = totalesGenerales(rows);
  return {
    filas,
    totalUnidades: registrosVigentes(rows).reduce((s, r) => s + unidadesDePremio(r), 0),
    // El total de canjes sale del módulo de totales, NO de sumar la columna: la columna cuenta un
    // canje una vez por cada premio distinto que entregó, así que sumarla daría más.
    totalCanjes: t.canjes,
    dias: fechas.size,
    premios: premios.size,
  };
}


export interface ResumenFecha {
  fecha: string;
  registros: number;
  canjes: number;
  monto: number;
  /** El corrido: canjes de este día más los de todos los días anteriores del reporte. */
  canjesAcumulados: number;
}

export function resumenPorFecha(rows: CanjeRow[]): ResumenFecha[] {
  const acc = new Map<string, ResumenFecha>();
  for (const r of registrosVigentes(rows)) {
    const f = acc.get(r.fecha)
      ?? { fecha: r.fecha, registros: 0, canjes: 0, monto: 0, canjesAcumulados: 0 };
    f.registros += 1;
    f.canjes += r.totalCanjes;
    f.monto += r.montoCompra ?? 0;
    acc.set(r.fecha, f);
  }

  // `fecha` ya viene como 'yyyy-MM-dd' en hora de Lima: comparada como texto ordena igual que
  // como fecha, y sin construir un `Date` que en otra zona corra el día.
  const filas = [...acc.values()].sort((a, b) => a.fecha.localeCompare(b.fecha));

  // El acumulado se calcula DESPUÉS de ordenar. Al revés daría un corrido sobre el orden en que
  // llegaron las filas, que no es cronológico: la consulta las trae por fecha descendente.
  let corrido = 0;
  for (const f of filas) {
    corrido += f.canjes;
    f.canjesAcumulados = corrido;
  }
  return filas;
}

export interface ResumenPremio {
  premio: string;
  unidades: number;
  /** En cuántos registros aparece. Un registro que lo repite en dos líneas cuenta una vez. */
  registros: number;
}

export function resumenPorPremio(rows: CanjeRow[]): ResumenPremio[] {
  const acc = new Map<string, ResumenPremio>();
  for (const r of registrosVigentes(rows)) {
    // `premiosDelCanje` ya agregó por nombre dentro del canje, así que acá cada nombre aparece
    // una sola vez por registro: el conteo de registros sale correcto sin un `Set` aparte.
    for (const p of premiosDelCanje(r)) {
      const f = acc.get(p.nombre) ?? { premio: p.nombre, unidades: 0, registros: 0 };
      f.unidades += p.unidades;
      f.registros += 1;
      acc.set(p.nombre, f);
    }
  }
  return ordenar([...acc.values()], (f) => f.unidades, (f) => f.premio);
}

export interface ResumenDinamica {
  /** Rótulo ya armado (`RG_DOWNY · Raspa y Gana`). Se conserva: hay quien ya lo lee así. */
  dinamica: string;
  /**
   * Código y nombre POR SEPARADO, que es lo que el diseño necesita para pintar el código arriba en
   * monoespaciada y el nombre debajo. Antes solo existía la cadena unida y había que volver a
   * partirla por ' · ' — frágil, porque el nombre de una dinámica puede contener ese separador.
   */
  codigo: string;
  nombre: string;
  registros: number;
  canjes: number;
}

export function resumenPorDinamica(rows: CanjeRow[]): ResumenDinamica[] {
  const acc = new Map<string, ResumenDinamica>();
  for (const r of registrosVigentes(rows)) {
    const vistas = new Set<string>();
    for (const l of r.lineas) {
      // Por ID, no por nombre: `campaign_dynamics` es `unique (campaign_id, code)` —nada impide
      // dos dinámicas homónimas—. El `dynamicId` viene de la propia línea, así que no se pierde
      // ni cuando RLS deja el embed de la dinámica en `null`.
      const clave = l.dynamicId;
      const f = acc.get(clave) ?? {
        dinamica: rotuloConCodigo(l.dynamicCode, l.dynamicName, 'Sin dinámica'),
        codigo: (l.dynamicCode ?? '').trim(),
        nombre: rotulo(l.dynamicName, 'Sin dinámica'),
        registros: 0,
        canjes: 0,
      };
      f.canjes += l.canjes;
      if (!vistas.has(clave)) {
        f.registros += 1;
        vistas.add(clave);
      }
      acc.set(clave, f);
    }
  }
  return ordenar([...acc.values()], (f) => f.canjes, (f) => f.dinamica);
}

export interface ResumenTienda {
  tienda: string;
  registros: number;
  canjes: number;
  unidades: number;
  monto: number;
}

export function resumenPorTienda(rows: CanjeRow[]): ResumenTienda[] {
  const acc = new Map<string, ResumenTienda>();
  for (const r of registrosVigentes(rows)) {
    // Bajo RLS un embed sin permiso devuelve `null` y la fila existe igual (BITACORA §3.7): esas
    // caen todas en la clave vacía y se rotulan "Sin tienda". Si se descartaran, la suma de la
    // tabla no daría el total general y nadie sabría por qué.
    const clave = r.pdv?.id ?? '';
    const tienda = tiendaDe(r);
    const f = acc.get(clave) ?? { tienda, registros: 0, canjes: 0, unidades: 0, monto: 0 };
    f.registros += 1;
    f.canjes += r.totalCanjes;
    f.unidades += unidadesDePremio(r);
    f.monto += r.montoCompra ?? 0;
    acc.set(clave, f);
  }
  return ordenar([...acc.values()], (f) => f.canjes, (f) => f.tienda);
}


export const LOGO_TREID = '<img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAPAAAABZCAMAAAA3mFmGAAAC/VBMVEUAAAAQHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy0QHy1/uIAzAAAA/nRSTlMAAAQJAhy0/p0sUm2HhXJRJwYqhsv29MR+JQEDSsb5+ERC3tRBGMOu9fJfD7qfEjfXLVfqPmbzS+PrOCgMJs0X5/riRwWojAru6Uno4ImAGqGlGYLf/d19EIu3zrU8MUAwFh0eTFtGPzsgcaO2omoTBzRok6/P3Nvk7di4XOH3vDkIqY4bY9qnTZTx/O+zVSuXqqAUKX/MntDHFdNuIvsjMy8uC2dYwlnmT8DR1dI1VvCWYGLZYRFr1lCmgTpOkWTFwQ2wH6tlvuWsNnw9SHVzdnAke727ubJ6jZpvU4RpVHmcj7+Vyq13kA6kWkUhdMlskoPsm16xmYrIMl14mBvjMQoAAAwtSURBVHja7Zx7XFR1FsA5w7AjZciggNxRRpBnUzwkCUHS0UEkCZQYBFFUfGYlRICgiIwiKuOjAMVHDgg9tVhyTczVVHq4Gj7SjbTaXpttj3Vxc3Nr253P3hlgvHPvuS+YUeHT+fPOb87vfH/v3znnXgeHO1xA4ugohZ780el3RobIBtxuHj5a57vuHniPyyBXuWjmPggM4DZ4iLvJTg/PoV6ESOS+BwyKYcO9uy1VDh+hEEfc94AJH1+qrSP9QBRxnwMG/wBrYwP9+zmwS5C1scH3iurivgYMqvvo1t4f0q+BQ8Po1oaF92vgURF0a5UP9Gvg0ZEMc31+A+5PwA9G0a0dE21DYJB4xTDEa2xPTu02Ao59iG7tcC9bAo8br6SLesLE2wisiaNbOynepsCTmT8nPHz7gB1g2BRraxJH2PKkdQcCOz6iphoTlOTYv4EdIHmq8qYt7tPEHaX7IjCkPJqq7bREnTY93bb34TsQmCRW+GXMyExNy5w5cJZc3OWwTwKbzJImZ/nMnjNXrLujzwL3pq1+A+4vwCBNz3aNjY11naeSUOy9c4BB4hQ+e/b8BQsXLc7u7dkVJI89vCTj8ZkBJnniyUlL/XK6p7uNgIGQahChLipkEYkmN1dOIH0hGftU3tP5AQWFy4qKE5ct91zx9L1ucszPDrzVkPUokksGDF9JPZ6UFq4aXKYzF7MVsHREHENWrykHixXxc0PXrgkcUrFOSm8qXfn6DUOiKrXUKvQbEzdtTtYwGge2PPMso56qamq7amq2btsoYxitnlAbHU/YDlgxWiujizGspqsg4e+TsT1SqSf/vMMaGHY+cM8u8w8MI0ozn9tN33fAp8jIqKdynqUUELuXpBqMqOgTMuoIYAN+rL5TFgYzf5783J56ijzV0EieBZ5nNqpxeScw5LwQaGk3KjBAcsm0jUZW0b/4kgpowC8zi0VagCH+lb16Dn17J2rYgJMK9pnlVeT/Bt/t+yjy2qotXMCgaKqiDJObwECM/f2MZiOnRLzuZj1BuYAB0odGcetL2y8vx4FrZUbB8nITBzA4/uEAdXpagEGStS6BV7feczY1lsINnPx4JJ++l9/YjQMPEAP8FDswqJKKrZ51AQNxsCVNK0T7oTd1IAgYsuP0/OrGD72P+dCGwDszaItAFzDhVlsqUH1UiQYEAMO8wwYh6oIRDTYDjon/I33R6wSGI/cJ6t5ONUcto5odGNLfEtC/LGIz4IZjjF3NBAxQdlyEfuOrRyznJDZg0Cyt7DGvzYDD1voynpHAANHHxRl0oo4PmIhO6zmvzYCjwpijbIcUiKzhIg0ybGgFTmBIf1vMkLEXMCYk8DubRBtX3BUTZQVe2yxW5a0DHvuWWlhRqgS4monZgMtX9YbXvsBwbzH2XBs0PuDtd/dO8MBXb+10HRfwex5cOFotz5ZgT2BPr5PIU33q4c3J6a2tquRX1v0J1bSywdTFOLDT3FOs9Xn4Zk6rqpp6vGIyh4EkcJUY4NMigDPfNyAmV7XJCdPWA+RtcT66ZWnXyFmBkxdNMeISOcOlvlGqUCjkrvvXsRUyA5852ymPuiNaBpw7S5HzS/1FAG88wHyW+AHlUgTEnsPYCFw5hw04+MJFlspGtqRYrtSguuvPbDby3IenYPdhIcDmSzJTivOsEuoAarBRr84ghwAKXNn0IV5d+yyNlcvF7X6lES/ZAwcAD3BpROKQj/Lz89uZI8b7EVr4D2BhIaIiM5YNeBh+yGp3tfbSA6TU3hpgQ+JHl/z8dbrcXN0g5nJ6/AJdHUiwc3FlHgAKHLQV3efS2uhRCYCYE7cC2PfyiO4In+JjxvR0P8t01kFTIqJnmgoHVq/Arg3KTySI4k/H2B1Yf2qRZchCSCbj99Q6RgeTS/VhRN2haBxYloDV/eE4JOwEjnFYWVsCB/+l/KaTBtomMOoKpMV3QTI2+4XalZiuz3BgI1r1fjTwRMwOxhTYDjjy/VZKQ0MLozr9ecrIA5CMa1j7blEQfjKamoMDY7JrD+rMh2rsomY7YMPHB6kDCy4zSqk/tzjcQbN72BenOPxcr7kKB/5ShQPnTrcrcEGs9cZwmFHCO7qrRaRb3vjqZISRS0ofFg58Dg8cAzxoT2Cli5XbHUJWMIvUkGuUpHVPS+AuDg+1WfSlHwgGrixhCU9BVpEdgQvLrTu4/K8IsGPykYsnxnvwnNSUCQWX51cLBj7AloAJbiPtCPx1PC+w4XzGlWA+95v3shWP+oVoCBAMHHaEDdhppv2AZSVSXmB+cX9ogMsDKg4Xj0hgGwXTMODK09BLYK1HwKTNpthVt4Y7G3jZFtqZQhSwzD3hxMUjTo3UmKlg4OWL2YDrkI3YVsDLa2jA1e2CcTeGfdNS06qhHZcEAxc9yAbcgPhz7QaM7MOoRHi+fqxGQjAPh4KBle/hwACzEPeXvYAd4GshtPlDj8ZqWOwVfPAYiL+ZCNK1SGH7Af8N8RhRRFu6cvWx6BQKLbH42ybKuBYO/MQ8HPi7728p8KccZ0dDwmsf/2CdY+QA6W/ri75usGTC4MDeyLONf0fPlnAVC5nbD/gq4sLrtDnt5Joz/hKp9bQFxT8iyX4/9IWzhGMfNnRgl6uv0NwnxUXMO2I3YAcJEo0mLV5++KzPTuakA5iYai6gf8jlHYLVEa+sNSBKixYjF2KIGYkZwAc8rMfAiiSsgYOu5TCzt0y8TVe6i0QGPJ8uZXHiRbZgzkjZR8w31iD+n1jb8AFXDuoxMLEIczDK2lOwJDSifgelUPOHmw8SuNfSrwKjUP6oo88oect1rCQfsDGPsR4IBYbqTKxCw4BkZhvK265Yl/JY7adBHfENU1GM6+espzHk5rGsmXzAz6h6DJy7Fa1R+a9sug85JC+VUWxlHTqks3/CXULFGdTMNkg5z+ZN4QNOrYfuzCjpvHJzkEsoMISOR6t0D4xW3EQG0Eychoy+md/5obGl8AIcRH2y5TEgzAIhowK9WXitgNFBf2O3BICQylujB2aek4oAdoCd6/A6tRVJu3WmXYlsRPlBnycnIPoi38QXrWzJZTaU4IJ/v+fX1ta24GfPCHYXAwU4B2077fZroc5ZowbuuK42nvIXBQyfs8XqvZc9vaC+7uqF8EGPdDSjY3RqNVt8eGKikU30HpNJCeYMwlOAVTNY1KjVar3ZqsQmMcBkF7PHcmV6ZeG+tCA9S/j6wCxgA3asNfZGKMCOa3gLbybnnnBgB1gwhk8lLsWfaViBIdRXvEIUWFrCGwb9qlEcsOp74TlpFFFvUAF7UotkcA8SRzBgqI/gK93hLwrYAeb0pDe0K1JYMwBMiWlOJ8XrRIDJZfoUX+mgUJHAkjwPPp1M3s5gLzswzOpFZhoVGI7xjRX1JwpRwOSd70vRFs1w40xbMsVp3pgiVisGTN4vPPlKx+WCOGC4sMIoSgw33LgT00w/xU/nzZY2iQfSLlbA8Fwwj4oKJ3HApM6sX8Twemzodl9w5kuHDBaQX1p8D5LDZvUNAHLj5Fmor68XCUwS+xQYhYosbYnlFVLujPixS3lHdfElL76PHpDdsZ1biXqgWGByuysTmixYemO2BIQBg+6lTO6ozRQXx3LerzyA9JUKTi3G9kaxwORlN+bJIKMA6bib6o3je6tF+p8vuSZg2E+NQj5rAZr1mUYu2TVHNLDparyEdyORHbi0xcrzwwNsmoCfB6jxKShzX1cmAUHf8QBF06ZSdrOKH7/aA2AH0J0O5EqANFbu2xpD+wYcLzDZyU6/rhrjzSikbW5/M4f/zTTLAPS/tIupxCTKinePOkp7AkzOudZB+VEs50z3A8+W+DPc8fzA5tdKR/zsWdhMMUgdUfD9oPSudw+xcGkt0zjFnKS9jBC9uvBGUplO1G2JpvbgsG9fZPoYmofc/0ko9oE/IcDmDspZdOznqsATVzo6OgK+qZ0+yrV7ZsDcNds66PLLRcw2Sd3RofnLSrWdoh+/L+7HXxfPJbraY7ReSxfZIV5g0z+95v83bltC95/cfWf879z68EaWFA2/KCOjnmY0yACE406vcGdnZ9dqamIUaN4xPaRJOW4bkZvsHHrms5aWlqV3lWXFjNPd3C6kW1oYcu0HYZ/2Avm42KyykmvkX55vOx2enS5hX9yd9l9j1DO6tffvM/8fWqt1bWkWivgAAAAASUVORK5CYII=" alt="Treid" width="81" height="30">';


/* ------------------------------------------------------------------ *
 * Estilos
 *
 * Tokens exactos del handoff de diseño. Radio 0 en todo el reporte y sin sombras: es un
 * documento, no una aplicación. La jerarquía la sostienen las hairlines y el fondo.
 *
 * Las fuentes del diseño (Archivo e IBM Plex Mono) NO son fuentes de sistema y NO van
 * incrustadas: habría que meter dos binarios al repositorio y son ~200 KB en base64. Se declaran
 * con su pila de respaldo, así que el reporte se ve con la fuente del sistema hasta que se
 * incrusten. Es lo único del diseño que queda pendiente y está anotado en PENDIENTES.
 * ------------------------------------------------------------------ */

const ESTILOS = `
:root {
  --navy: #101F2D; --navy-hondo: #0B1721; --navy-borde: #2B3A48;
  --papel: #F5F3EE; --papel-pie: #EFEBE3; --blanco: #FFFFFF; --tenue: #FAF8F4;
  --riel: #F1EEE7; --hairline: #E3DFD6; --hairline-suave: #F1EEE7; --control: #CFC9BC;
  --tinta-2: #46525C; --tinta-3: #6E7A85; --tinta-4: #8B959E;
  --sobre-claro: #C9D3DB; --sobre-medio: #A9B7C2; --sobre-tenue: #8FA0AE; --sobre-tenue-2: #7E90A0;
  --ambar: #D8A25E;
  --alerta-fondo: #F6E9D6; --alerta-texto: #8A4E14;
  --anulado-fondo: #F3DCDC; --anulado-texto: #8C2020;
  --mono: 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
* { box-sizing: border-box; }
html { -webkit-text-size-adjust: 100%; }
body {
  margin: 0; background: var(--papel); color: var(--navy);
  font-family: Archivo, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px; line-height: 1.55; text-wrap: pretty;
}
.limite { max-width: 1160px; margin: 0 auto; padding: 0 40px; }
a { color: #1E4B6B; }
a:hover { color: var(--navy); }
h1, h2, h3, h4 { margin: 0; font-weight: 600; }
.mono { font-family: var(--mono); }
.num { font-variant-numeric: tabular-nums; }

/* ---- Portada ---- */
.portada { background: var(--navy); color: var(--papel); }
.portada .limite { padding: 34px 40px 40px; }
.portada-alto { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; }
.portada-alto img { height: 26px; width: auto; display: block; filter: brightness(0) invert(1); }
.portada-sello { text-align: right; font-family: var(--mono); font-size: 11px;
  letter-spacing: .14em; text-transform: uppercase; color: var(--sobre-tenue); }
.portada-titulo { margin-top: 52px; display: flex; align-items: flex-end;
  justify-content: space-between; gap: 40px; flex-wrap: wrap; }
.sobretitulo { font-family: var(--mono); font-size: 11px; letter-spacing: .14em;
  text-transform: uppercase; color: var(--sobre-tenue); margin-bottom: 10px; }
.portada h1 { font-size: 52px; line-height: 1.02; font-weight: 600; letter-spacing: -.022em; }
.portada-periodo { font-size: 26px; font-weight: 500; letter-spacing: -.01em;
  color: var(--sobre-claro); padding-bottom: 4px; }
.portada dl { margin: 44px 0 0; padding-top: 26px; border-top: 1px solid var(--navy-borde);
  display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 22px 28px; }
.portada dt { font-family: var(--mono); font-size: 10.5px; letter-spacing: .13em;
  text-transform: uppercase; color: var(--sobre-tenue-2); }
.portada dd { margin: 6px 0 0; font-size: 15px; font-weight: 500; color: var(--papel);
  overflow-wrap: anywhere; }
.reservado { background: var(--navy-hondo); border-top: 1px solid var(--navy-borde); }
.reservado .limite { padding: 14px 40px; display: flex; gap: 14px; align-items: baseline;
  flex-wrap: wrap; }
.reservado b { font-family: var(--mono); font-size: 10.5px; letter-spacing: .13em;
  text-transform: uppercase; color: var(--ambar); flex: 0 0 auto; font-weight: 500; }
.reservado p { margin: 0; font-size: 13px; color: var(--sobre-medio); flex: 1 1 320px; }

/* ---- Encabezados de sección ---- */
.seccion { padding-top: 48px; }
.seccion-kpi { padding-top: 56px; }
.seccion-cabeza { display: flex; justify-content: space-between; align-items: baseline;
  gap: 20px; flex-wrap: wrap; }
.seccion-rotulo { font-family: var(--mono); font-size: 13px; font-weight: 500;
  letter-spacing: .14em; text-transform: uppercase; color: var(--tinta-3); }
.seccion-alcance { font-size: 12.5px; color: var(--tinta-3); }
.seccion h3 { font-size: 22px; letter-spacing: -.015em; }
.nota { margin: 14px 0 0; font-size: 12.5px; color: var(--tinta-3); max-width: 760px; }
.nota + .nota { margin-top: 6px; }

/* ---- KPIs ---- */
/* Las hairlines salen del gap de 1px sobre el fondo hairline con celdas blancas: no hay bordes
   por celda ni tarjetas redondeadas. */
.kpis { margin-top: 18px; display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 1px; background: var(--hairline); border: 1px solid var(--hairline); }
.kpi { background: var(--blanco); padding: 22px 24px 20px; }
.kpi-valor { display: block; font-size: 44px; line-height: 1; font-weight: 600;
  letter-spacing: -.03em; font-variant-numeric: tabular-nums; }
.kpi-rotulo { display: block; font-family: var(--mono); font-size: 10.5px; letter-spacing: .12em;
  text-transform: uppercase; color: var(--tinta-3); margin-top: 12px; }
.kpi-detalle { display: block; font-size: 12.5px; color: var(--tinta-4); margin-top: 4px; }

/* ---- Gráfico ---- */
.tarjeta-blanca { background: var(--blanco); border: 1px solid var(--hairline); }
.grafico { padding: 28px 30px; display: grid; gap: 20px; }
.barra-alto { display: flex; justify-content: space-between; align-items: baseline; gap: 12px;
  margin-bottom: 8px; }
.barra-nombre { font-size: 16px; font-weight: 500; }
.barra-detalle { font-size: 13px; color: var(--tinta-3); }
.barra-fila { display: flex; align-items: center; gap: 14px; }
.barra-riel { flex: 1 1 auto; height: 26px; background: var(--riel); }
.barra-relleno { background: var(--navy); height: 100%; min-width: 3px; transition: width .3s ease; }
.barra-cifra { flex: 0 0 58px; text-align: right; font-size: 22px; font-weight: 600;
  font-variant-numeric: tabular-nums; letter-spacing: -.02em; }
.grafico-pie { margin: 22px 0 0; padding-top: 18px; border-top: 1px solid var(--papel-pie);
  font-size: 12.5px; color: var(--tinta-3); }

/* ---- Tablas ---- */
.desglose { display: grid; gap: 26px; margin-top: 20px; }
.par { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 26px; }
/* "min-width: 0" en los hijos del grid, o el "overflow-x: auto" de .ancha NO recorta nada.
   Un hijo de grid tiene "min-width: auto", o sea que se niega a ser mas angosto que su contenido:
   la tabla con "min-width: 560px" empujaba la tarjeta a 578px y en un celular de 375px el
   documento ENTERO scrolleaba en horizontal, en vez de scrollear solo la tabla. */
.desglose > *, .par > * { min-width: 0; }
.tabla-cabeza { padding: 20px 24px 0; }
.tabla-cabeza h4 { font-family: var(--mono); font-size: 11px; font-weight: 500;
  letter-spacing: .13em; text-transform: uppercase; color: var(--tinta-3); }
.tabla-caja { padding: 12px 8px 8px; }
.ancha { overflow-x: auto; max-width: 100%; }
.ancha table { min-width: 560px; }
table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
th { padding: 8px 16px; border-bottom: 1px solid var(--hairline); text-align: left;
  font-family: var(--mono); font-size: 10.5px; font-weight: 500; letter-spacing: .1em;
  text-transform: uppercase; color: var(--tinta-3); white-space: nowrap; }
td { padding: 10px 16px; border-bottom: 1px solid var(--hairline-suave); }
th.der, td.der { text-align: right; font-variant-numeric: tabular-nums; }
td.tienda { color: var(--tinta-2); }
td.premio { font-weight: 500; }
td.apoyo { color: var(--tinta-3); }
td.vacio { color: var(--tinta-3); text-align: center; padding: 24px 16px; }
tr.total td { border-top: 2px solid var(--navy); border-bottom: 0; padding: 12px 16px;
  font-weight: 600; }
tr.total td.apoyo { font-weight: 400; }
.din-codigo { display: block; font-family: var(--mono); font-size: 11px; color: var(--tinta-3);
  letter-spacing: .06em; }
.din-nombre { display: block; margin-top: 2px; }


/* ---- Filtros ---- */
.filtros { position: sticky; top: 0; z-index: 20; background: var(--papel);
  padding: 12px 0 14px; margin-bottom: 22px; border-bottom: 1px solid var(--hairline); }
.filtros-fila { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; }
.filtros label { display: block; }
.filtros span { display: block; font-family: var(--mono); font-size: 10px; letter-spacing: .12em;
  text-transform: uppercase; color: var(--tinta-3); margin-bottom: 5px; }
.filtros select {
  appearance: none; -webkit-appearance: none; background-color: var(--blanco);
  border: 1px solid var(--control); color: var(--navy); font: inherit; font-size: 14px;
  padding: 9px 34px 9px 12px; cursor: pointer; border-radius: 0;
  background-image: linear-gradient(45deg, transparent 50%, #101F2D 50%),
                    linear-gradient(135deg, #101F2D 50%, transparent 50%);
  background-position: calc(100% - 17px) 55%, calc(100% - 12px) 55%;
  background-size: 5px 5px, 5px 5px; background-repeat: no-repeat;
}
.f-fecha { min-width: 150px; } .f-tienda { min-width: 190px; } .f-premio { min-width: 150px; }
.limpiar { background: none; border: 0; border-bottom: 1px solid var(--navy); color: var(--navy);
  font: inherit; font-size: 13px; padding: 0 0 2px; margin-bottom: 11px; cursor: pointer; }
.limpiar:hover { color: var(--tinta-4); border-color: var(--tinta-4); }
.imprimir { margin-left: auto; margin-bottom: 11px; background: var(--navy); border: 0;
  color: var(--papel); font: inherit; font-size: 13px; padding: 9px 14px; cursor: pointer; }
.imprimir:hover { background: var(--navy-hondo); }
.filtros-aviso { margin: 12px 0 0; font-size: 12.5px; color: var(--tinta-3); }
[hidden] { display: none !important; }

/* ---- Tarjetas de canje ---- */
.canjes { display: grid; gap: 18px; }
.canje { background: var(--blanco); border: 1px solid var(--hairline);
  display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 30px; padding: 24px 26px; }
.canje.anulado { opacity: .62; }
.boleta-fila { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.rotulo-mini { font-family: var(--mono); font-size: 10.5px; letter-spacing: .12em;
  text-transform: uppercase; color: var(--tinta-4); }
.boleta-num { font-family: var(--mono); font-size: 21px; font-weight: 500; letter-spacing: -.01em;
  overflow-wrap: anywhere; }
.boleta-sin { font-size: 15px; color: var(--tinta-4); }
.chip { font-size: 12px; font-weight: 500; padding: 3px 9px; }
.chip-ambar { background: var(--alerta-fondo); color: var(--alerta-texto); }
.chip-rojo { background: var(--anulado-fondo); color: var(--anulado-texto); }
.premio-fila { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--hairline-suave);
  display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.chip-premio { background: var(--navy); color: var(--papel); font-size: 15px; font-weight: 600;
  padding: 4px 11px; }
.premio-din { font-size: 13.5px; color: var(--tinta-3); }
.canje dl { margin: 20px 0 0; display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 16px 24px; }
.canje dt { font-family: var(--mono); font-size: 10px; letter-spacing: .12em;
  text-transform: uppercase; color: var(--tinta-4); }
.canje dd { margin: 5px 0 0; font-size: 14px; overflow-wrap: anywhere; }
.obs { margin-top: 20px; padding: 14px 16px; background: var(--tenue);
  border-left: 2px solid var(--control); }
.obs-rotulo { font-family: var(--mono); font-size: 10px; letter-spacing: .12em;
  text-transform: uppercase; color: var(--tinta-4); margin-bottom: 6px; }
.obs p { margin: 0; font-size: 14px; color: var(--tinta-2); white-space: pre-line; }
.evidencia { display: grid; gap: 12px; align-content: start; }
.evidencia figure { margin: 0; }
.evidencia figcaption { font-family: var(--mono); font-size: 10px; letter-spacing: .12em;
  text-transform: uppercase; color: var(--tinta-4); margin-bottom: 7px; }
.evidencia button { display: block; padding: 0; border: 1px solid var(--hairline);
  background: var(--riel); line-height: 0; cursor: zoom-in; }
.evidencia button:hover { border-color: var(--navy); }
.evidencia img { width: 190px; height: 190px; object-fit: cover; display: block; }
.foto-falta { width: 190px; padding: 12px; font-size: 12.5px; color: var(--tinta-3);
  border: 1px solid var(--hairline); background: var(--riel); line-height: 1.45; }
.dia { display: flex; align-items: baseline; gap: 14px; padding: 14px 0 6px; }
.dia-titulo { font-size: 17px; font-weight: 600; letter-spacing: -.01em; }
.dia-regla { flex: 1 1 auto; height: 1px; background: var(--hairline); }
.dia-cuenta { font-size: 12.5px; color: var(--tinta-3); }
.sin-resultados { text-align: center; padding: 44px 0; font-size: 14px; color: var(--tinta-3); }

/* ---- Lupa de evidencia ---- */
.lupa { position: fixed; inset: 0; z-index: 100; background: rgba(11, 23, 33, .93);
  display: flex; align-items: center; justify-content: center; flex-direction: column;
  padding: 40px; cursor: zoom-out; animation: apareceLupa .18s ease; }
@keyframes apareceLupa { from { opacity: 0; } to { opacity: 1; } }
.lupa img { max-width: 86vw; max-height: 78vh; object-fit: contain; background: var(--navy); }
.lupa-pie { margin-top: 14px; font-size: 13px; color: var(--sobre-claro); text-align: center; }
.lupa-pie small { display: block; margin-top: 4px; font-size: 12px; color: var(--sobre-tenue-2); }

/* ---- Pie ---- */
.pie { margin-top: 56px; border-top: 1px solid var(--hairline); background: var(--papel-pie); }
.pie .limite { padding: 26px 40px 34px; display: flex; justify-content: space-between; gap: 20px;
  flex-wrap: wrap; font-size: 12.5px; color: var(--tinta-3); }
.pie-marca { font-family: var(--mono); letter-spacing: .1em; text-transform: uppercase; }

/* ---- Angosto: la tarjeta pasa a una columna y el titular baja ---- */
@media (max-width: 700px) {
  .limite { padding: 0 20px; }
  .portada .limite, .reservado .limite, .pie .limite { padding-left: 20px; padding-right: 20px; }
  .portada h1 { font-size: 34px; }
  .portada-periodo { font-size: 20px; }
  .canje { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .evidencia button, .evidencia figure { width: 100%; }
  .evidencia img, .foto-falta { width: 100%; height: auto; }
  /* Sin la proporcion la imagen colapsa a la altura del alt mientras carga y la tarjeta salta. */
  .evidencia img { aspect-ratio: 1; }
}

/* ---- Impresion: de aca sale el PDF ---- */
@media print {
  /* Sin esto el navegador descarta los fondos y el PDF sale en blanco: todo el diseno se sostiene
     en el fondo papel y los chips tintados. No es cosmetico, es que el documento pierde sentido. */
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  @page { size: A4; margin: 14mm; }
  body { background: var(--blanco); font-size: 11px; }
  .limite { max-width: none; padding: 0; }
  /* La barra pegada al borde superior se imprimiria flotando encima del contenido en cada hoja. */
  .filtros { position: static; }
  /* Los controles no existen en papel. El aviso de filtros SI se queda: dice que recorte se
     imprimio, y sin el el PDF miente por omision. */
  .filtros select, .filtros label, .limpiar, .imprimir, .lupa { display: none !important; }
  .filtros { border-bottom: 0; margin-bottom: 0; padding: 0; }
  /* Una A4 con margen de 14mm deja ~688px utiles, o sea que la consulta de "angosto"
     (max-width: 700px) SE ACTIVA al imprimir: la tarjeta pasaria a una columna, con la foto
     debajo del contenido, y quedaria mas alta que la hoja. Una unidad mas alta que la pagina
     obliga al navegador a partirla igual, ignorando el 'break-inside: avoid' -- justo el corte que
     se quiere evitar. Se fuerza el formato de dos columnas, que es el compacto. */
  .canje { grid-template-columns: minmax(0, 1fr) auto; gap: 20px; }
  .evidencia button, .evidencia figure { width: auto; }
  .limite, .portada .limite, .reservado .limite, .pie .limite { padding-left: 0; padding-right: 0; }
  .portada h1 { font-size: 30px; }
  .portada-periodo { font-size: 16px; }
  .kpi-valor { font-size: 26px; }
  .seccion, .seccion-kpi { padding-top: 22px; }
  .canje { break-inside: avoid; page-break-inside: avoid; }
  .evidencia img { width: 130px; height: 130px; }
  /* Una tabla partida deja el encabezado en una hoja y las filas en la otra: quien la lee tiene
     que volver atras para saber que columna es cual. La solucion es que el encabezado SE REPITA en
     cada hoja, no que la tabla no se corte --con un mes de campana esas tablas pasan de 30 filas,
     asi que pedir que no se corten es pedir lo contrario de lo que se necesita--. Lo que si se pide
     es que no se parta por la mitad una FILA ni una cifra. */
  thead { display: table-header-group; }
  /* Lo que NO se puede partir por la mitad, de lo mas chico a lo mas grande.
     La regla de fondo: se parte lo que es una LISTA (una tabla larga se corta y repite su
     encabezado, que es lo correcto), y no se parte lo que es una UNIDAD DE LECTURA -- una fila, una
     cifra, una barra del grafico con su numero, una tarjeta con su foto. Una barra separada de su
     cifra, o un encabezado de tabla solo al pie de la hoja, no se leen: se vuelven ruido. */
  tr, .kpi, .barra, .obs, figure, .evidencia { break-inside: avoid; page-break-inside: avoid; }
  /* La rejilla de indicadores y la tarjeta del grafico son bloques cortos: entran enteros en una
     hoja, asi que partirlos solo genera una pagina con tres cifras huerfanas. */
  .kpis, .grafico { break-inside: avoid; page-break-inside: avoid; }
  /* Un titulo al final de la hoja y su contenido en la siguiente es el corte que mas molesta:
     se lee "Desglose" y hay que dar vuelta la pagina para ver de que. */
  h3, h4, .seccion-cabeza, .tabla-cabeza, .dia { break-after: avoid; page-break-after: avoid; }
  /* Y las dos tablas cortas tampoco se parten; la ancha SI puede, repitiendo su encabezado. */
  [data-bloque="actividad"], [data-bloque="dinamicas"] {
    break-inside: avoid; page-break-inside: avoid; }
  /* Nada de una sola linea de parrafo colgando sola al pie o al principio de una hoja. */
  p, .nota { orphans: 3; widows: 3; }
  .ancha { overflow-x: visible; }
  .ancha table { min-width: 0; }
}
`;


/* ------------------------------------------------------------------ *
 * El bloque de datos que leen los filtros
 * ------------------------------------------------------------------ */

/** Lo que el script del archivo necesita para recalcular. Es un subconjunto deliberado. */
interface CanjeSerializado {
  id: string;
  fecha: string;
  tienda: string;
  premios: { nombre: string; unidades: number }[];
  canjes: number;
  monto: number | null;
  /**
   * Los anulados van en la lista, marcados.
   *
   * Su tarjeta tiene que responder a los filtros —filtrar por una fecha no puede dejar visible el
   * anulado de otro día— pero NUNCA entran en un acumulado. El script filtra por esta bandera
   * antes de sumar cualquier cosa; la lista completa solo decide qué tarjeta se ve.
   */
  anulado: boolean;
}

/**
 * Serializa SOLO lo que los filtros necesitan, y lo escapa para vivir dentro de un `<script>`.
 *
 * **Dos decisiones que no son de estilo:**
 *
 * 1. **Subconjunto, no `CanjeRow` entero.** Volcar la fila completa metería `campaignId`, las rutas
 *    del Storage, `marcasDejadas` y los datos del trabajador en un archivo que sale de la empresa
 *    y se le manda a un cliente de marca. Lo que no hace falta para filtrar, no viaja.
 * 2. **Las fotos NO van acá.** Se quedan en el `<img>` del documento. Meter los `data:` URI en el
 *    JSON duplicaría el peso del archivo —y el archivo ya pesa 18 MB con 100 canjes—, además de
 *    obligar a re-renderizar imágenes al filtrar en vez de solo esconder tarjetas.
 *
 * **El escapado es otro contexto.** `escaparHtml` no sirve dentro de un `<script>`: ahí `&lt;` no
 * se decodifica y rompería el JSON. Lo que puede cerrar el bloque antes de tiempo es la secuencia
 * `</script>` (y el navegador la reconoce aunque venga partida o en mayúsculas), así que se escapa
 * el `<`. `U+2028` y `U+2029` son saltos de línea legales en JSON pero ilegales dentro de un
 * literal de JavaScript: sin escaparlos, una observación tecleada con uno de esos caracteres deja
 * el archivo sin filtros y con un error de sintaxis en la consola de quien lo abre.
 */
function serializarDatos(rows: CanjeRow[]): string {
  // Ordenado por `id`, NO en el orden en que llegaron las filas. Sin esto el mismo corte bajado
  // dos veces produce dos archivos con bytes distintos —el arreglo llega en el orden de la
  // consulta, que no esta garantizado— y quien recibe los dos no puede distinguir "se reordeno"
  // de "cambio un numero". Es el mismo motivo de los desempates de `ordenar`.
  const datos: CanjeSerializado[] = [...rows]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((r) => ({
      id: r.id,
      fecha: r.fecha,
      tienda: tiendaDe(r),
      premios: premiosDelCanje(r).map((p) => ({ nombre: p.nombre, unidades: p.unidades })),
      canjes: r.totalCanjes,
      monto: r.montoCompra,
      anulado: r.estado === 'anulado',
    }));
  return JSON.stringify(datos)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/* ------------------------------------------------------------------ *
 * Piezas del documento
 * ------------------------------------------------------------------ */

function kpi(clave: string, valor: string, rotuloTexto: string, detalle: string): string {
  return `<div class="kpi">`
    + `<span class="kpi-valor" data-kpi="${escaparHtml(clave)}">${escaparHtml(valor)}</span>`
    + `<span class="kpi-rotulo">${escaparHtml(rotuloTexto)}</span>`
    + `<span class="kpi-detalle" data-kpi-detalle="${escaparHtml(clave)}">`
    + `${escaparHtml(detalle)}</span>`
    + `</div>`;
}

/**
 * El texto de evidencia de la portada.
 *
 * Cuenta CANJES con evidencia, no archivos: el reporte lleva hasta dos fotos por canje, así que
 * contar archivos diría "34 de 34 canjes" con 17 registros.
 *
 * Pero si alguna foto no se pudo bajar, ESO tiene que decirse igual y en la portada: quien recibe
 * el archivo no tiene otra forma de saber que falta evidencia que sí existe en el sistema, y un
 * reporte que calla lo que le falta es peor que uno incompleto que lo declara.
 */
function evidenciaTexto(
  rows: CanjeRow[], fotos: FotosEmbebidas, conEvidencia: number, vigentes: number,
): string {
  const base = `${conEvidencia} de ${vigentes} canjes`;
  const archivos = rutasDeFotoDeTodos(rows);
  const faltan = archivos.filter((p) => !fotos.has(p)).length;
  return faltan === 0
    ? base
    : `${base} · ${plural(faltan, 'foto', 'fotos')} no se pudo incluir`;
}

function portada(
  meta: ReporteMeta, rows: CanjeRow[], fotos: FotosEmbebidas, generatedAt: Date,
): string {
  const vigentes = registrosVigentes(rows);
  const t = totalesGenerales(rows);
  const dins = resumenPorDinamica(rows);
  // La segunda línea del sello identifica la dinámica. Con varias no se elige una —esconder cuál
  // fue es peor que decir que son varias—; sin registros no se imprime: no hay identidad que
  // declarar y un sello vacío parece un error del generador.
  const sello = dins.length === 1
    ? rotulo(dins[0].codigo, dins[0].nombre)
    : dins.length > 1 ? `Multi-dinámica (${dins.length})` : '';

  const conEvidencia = vigentes.filter((r) => rutasDeFoto(r).some((p) => fotos.has(p))).length;
  const filtros = meta.filtros.length === 0 ? 'Sin filtros: todo el rango' : meta.filtros.join(' · ');

  const pares: [string, string][] = [
    ['Periodo', `${meta.periodStart} a ${meta.periodEnd}`],
    ['Filtros del reporte', filtros],
    ['Registros vigentes', `${t.registros} de ${t.registros + t.anulados}`],
    // Cuenta CANJES con evidencia, no archivos: el reporte lleva hasta dos fotos por canje, así
    // que contar archivos diría "34 de 34 canjes" con 17 registros.
    ['Evidencia fotográfica', evidenciaTexto(rows, fotos, conEvidencia, vigentes.length)],
    ['Generado el', formatInTimeZone(generatedAt, TZ, 'dd/MM/yyyy HH:mm')],
  ];

  return `<header class="portada">
  <div class="limite">
    <div class="portada-alto">
      ${LOGO_TREID}
      <div class="portada-sello"><div>Reporte de canjes</div>${
  sello === '' ? '' : `<div>${escaparHtml(sello)}</div>`}</div>
    </div>
    <div class="portada-titulo">
      <div>
        <div class="sobretitulo">Campaña</div>
        <h1>${escaparHtml(meta.campaignName)}</h1>
      </div>
      <div class="portada-periodo">${escaparHtml(
    `${fechaCorta(meta.periodStart)} – ${fechaCorta(meta.periodEnd)}`,
  )}</div>
    </div>
    <dl>${pares.map(([d, v]) => `<div><dt>${escaparHtml(d)}</dt><dd>${escaparHtml(v)}</dd></div>`)
    .join('')}</dl>
  </div>
</header>
<div class="reservado"><div class="limite">
  <b>Información reservada</b>
  <p>Las fotos van dentro del archivo: se abre sin internet y sin iniciar sesión. Contiene
  evidencia de la operación en tienda.</p>
</div></div>`;
}

function bloqueIndicadores(rows: CanjeRow[], conMonto: boolean): string {
  const t = totalesGenerales(rows);
  const vigentes = registrosVigentes(rows);
  const fechas = [...new Set(vigentes.map((r) => r.fecha))].sort();
  const tiendas = resumenPorTienda(rows);
  const unidades = vigentes.reduce((s, r) => s + unidadesDePremio(r), 0);

  const celdas = [
    kpi('registros', String(t.registros), 'Registros vigentes',
      `${plural(t.anulados, 'anulado', 'anulados')} fuera del cálculo`),
    kpi('canjes', String(t.canjes), 'Canjes', `${plural(unidades, 'unidad', 'unidades')} entregadas`),
    kpi('tiendas', String(tiendas.length), 'Tiendas activas',
      tiendas.length === 1 ? tiendas[0].tienda : 'con al menos un canje'),
    kpi('dias', String(fechas.length), 'Días con actividad',
      fechas.length === 0
        ? '—'
        : `${fechaCorta(fechas[0])} – ${fechaCorta(fechas[fechas.length - 1])}`),
  ];
  if (conMonto) {
    // Tercera posición, como pide el diseño: el monto se lee junto a los canjes, no al final.
    //
    // El ticket promedio divide entre los registros QUE TRAEN MONTO (`registrosConMonto`), no
    // entre todos. El prototipo del diseño divide entre todos y eso da un ticket falsamente bajo
    // en cuanto un solo registro viene sin monto anotado, que es lo normal.
    celdas.splice(2, 0, kpi('monto', soles(t.montoTotal), 'Monto total',
      t.ticketPromedio === null
        ? 'Ningún registro trae monto anotado'
        : `Ticket promedio ${soles(t.ticketPromedio)} · ${plural(t.registrosConMonto, 'registro', 'registros')}`));
  }

  return `<section class="seccion-kpi" data-bloque="indicadores">
  <div class="seccion-cabeza">
    <div class="seccion-rotulo">Indicadores</div>
    <div class="seccion-alcance" data-alcance>Toda la campaña · ${
  escaparHtml(plural(t.registros, 'registro vigente', 'registros vigentes'))}</div>
  </div>
  <div class="kpis">${celdas.join('')}</div>
  <p class="nota">Calculado sobre registros vigentes. Un canje anulado sigue visible en el detalle
  con su motivo, pero no suma en ningún acumulado.</p>${conMonto ? '' : `
  <p class="nota">Ninguna dinámica de este reporte registra el monto de la boleta, así que no se
  muestran monto total ni ticket promedio.</p>`}
</section>`;
}

function grafico(rows: CanjeRow[]): string {
  const premios = resumenPorPremio(rows);
  const total = premios.reduce((s, p) => s + p.unidades, 0);
  // Piso de 1 para no dividir por cero. El ancho es relativo al premio MÁS ALTO —el líder siempre
  // llega a 100%— mientras el porcentaje del texto es sobre el total: son dos números distintos y
  // confundirlos hace que las barras no se puedan comparar entre sí.
  const max = Math.max(1, ...premios.map((p) => p.unidades));

  const barras = premios.map((p) => `<div class="barra" data-premio="${escaparHtml(p.premio)}">
      <div class="barra-alto">
        <span class="barra-nombre">${escaparHtml(p.premio)}</span>
        <span class="barra-detalle">${escaparHtml(
    `${plural(p.registros, 'registro', 'registros')} · ${
      total === 0 ? 0 : Math.round((p.unidades / total) * 100)}% del total`,
  )}</span>
      </div>
      <div class="barra-fila">
        <div class="barra-riel"><div class="barra-relleno" style="width:${
  Math.round((p.unidades / max) * 100)}%"></div></div>
        <span class="barra-cifra num">${p.unidades}</span>
      </div>
    </div>`).join('');

  return `<section class="seccion" data-bloque="grafico">
  <h3>Unidades entregadas por premio</h3>
  <div class="tarjeta-blanca">
    <div class="grafico" data-barras>${
  premios.length === 0
    ? '<p class="sin-resultados">Ningún premio registrado.</p>'
    : barras}</div>
    <div class="limite" style="padding:0 30px 28px">
      <p class="grafico-pie">Un canje puede entregar más de una unidad, por eso el total de
      unidades no siempre coincide con el total de canjes.</p>
    </div>
  </div>
</section>`;
}

function tablaEntregas(rows: CanjeRow[]): string {
  const e = entregasPorTiendaFechaPremio(rows);
  const filas = e.filas.map((f) => `<tr data-fila="entrega">`
    + `<td class="tienda">${escaparHtml(f.tienda)}</td>`
    + `<td>${escaparHtml(fechaCorta(f.fechaIso))}</td>`
    + `<td class="premio">${escaparHtml(f.premio)}</td>`
    + `<td class="der">${f.unidades}</td>`
    + `<td class="der apoyo">${f.canjes}</td></tr>`).join('');

  return `<div class="tarjeta-blanca" data-bloque="entregas">
    <div class="tabla-cabeza"><h4>Entregas por tienda, fecha y premio</h4></div>
    <div class="tabla-caja ancha">
      <table>
        <thead><tr><th>Tienda</th><th>Fecha</th><th>Premio</th>
          <th class="der">Unidades</th><th class="der">Canjes</th></tr></thead>
        <tbody data-cuerpo="entregas">${
  filas === '' ? '<tr><td class="vacio" colspan="5">Ninguna entrega registrada.</td></tr>' : filas}
        </tbody>
        <tfoot><tr class="total" data-fila="total-entregas">
          <td>Total</td>
          <td class="apoyo" data-celda="dias">${escaparHtml(plural(e.dias, 'día', 'días'))}</td>
          <td class="apoyo" data-celda="premios">${
  escaparHtml(plural(e.premios, 'premio', 'premios'))}</td>
          <td class="der" data-celda="unidades">${e.totalUnidades}</td>
          <td class="der" data-celda="canjes">${e.totalCanjes}</td>
        </tr></tfoot>
      </table>
    </div>
  </div>`;
}

function tablaActividad(rows: CanjeRow[]): string {
  const filas = resumenPorFecha(rows).map((f) => `<tr data-fila="fecha">`
    + `<td>${escaparHtml(fechaCorta(f.fecha))}</td>`
    + `<td class="der">${f.registros}</td>`
    + `<td class="der">${f.canjes}</td>`
    + `<td class="der apoyo">${f.canjesAcumulados}</td></tr>`).join('');
  return `<div class="tarjeta-blanca" data-bloque="actividad">
    <div class="tabla-cabeza"><h4>Actividad por día</h4></div>
    <div class="tabla-caja">
      <table>
        <thead><tr><th>Fecha</th><th class="der">Registros</th><th class="der">Canjes</th>
          <th class="der">Acumulado</th></tr></thead>
        <tbody data-cuerpo="actividad">${
  filas === '' ? '<tr><td class="vacio" colspan="4">Ninguna fecha con registros.</td></tr>' : filas}
        </tbody>
      </table>
    </div>
  </div>`;
}

function tablaDinamicas(rows: CanjeRow[]): string {
  const filas = resumenPorDinamica(rows).map((f) => `<tr data-fila="dinamica"><td>${
  f.codigo === '' ? '' : `<span class="din-codigo">${escaparHtml(f.codigo)}</span>`}`
    + `<span class="din-nombre">${escaparHtml(f.nombre)}</span></td>`
    + `<td class="der">${f.registros}</td>`
    + `<td class="der">${f.canjes}</td></tr>`).join('');
  return `<div class="tarjeta-blanca" data-bloque="dinamicas">
    <div class="tabla-cabeza"><h4>Dinámicas</h4></div>
    <div class="tabla-caja">
      <table>
        <thead><tr><th>Dinámica</th><th class="der">Registros</th>
          <th class="der">Canjes</th></tr></thead>
        <tbody>${
  filas === '' ? '<tr><td class="vacio" colspan="3">Ninguna dinámica registrada.</td></tr>' : filas}
        </tbody>
      </table>
    </div>
  </div>`;
}

function desglose(rows: CanjeRow[]): string {
  return `<section class="seccion" data-bloque="desglose">
  <h3>Desglose</h3>
  <div class="desglose">
    ${tablaEntregas(rows)}
    <div class="par">${tablaActividad(rows)}${tablaDinamicas(rows)}</div>
  </div>
</section>`;
}


/* ------------------------------------------------------------------ *
 * Filtros y tarjetas
 * ------------------------------------------------------------------ */

function barraFiltros(rows: CanjeRow[]): string {
  const vigentes = registrosVigentes(rows);
  // Las opciones salen de TODOS los registros vigentes, no del recorte visible: si se llenaran
  // desde el conjunto filtrado, elegir una tienda borraría las demás de la lista y no habría cómo
  // volver sin recargar el archivo.
  const fechas = [...new Set(vigentes.map((r) => r.fecha))].sort();
  const tiendas = [...new Set(vigentes.map(tiendaDe))].sort((a, b) => a.localeCompare(b, 'es'));
  const premios = [...new Set(vigentes.flatMap((r) => premiosDelCanje(r).map((p) => p.nombre)))]
    .sort((a, b) => a.localeCompare(b, 'es'));

  const opciones = (todas: string, valores: string[], etiqueta?: (v: string) => string): string =>
    `<option value="">${escaparHtml(todas)}</option>`
    + valores.map((v) => `<option value="${escaparHtml(v)}">${
      escaparHtml(etiqueta === undefined ? v : etiqueta(v))}</option>`).join('');

  return `<div class="filtros" data-bloque="filtros">
    <div class="filtros-fila">
      <label class="f-fecha"><span>Fecha</span>
        <select data-filtro="fecha">${opciones('Todas las fechas', fechas, fechaCorta)}</select>
      </label>
      <label class="f-tienda"><span>Tienda</span>
        <select data-filtro="tienda">${opciones('Todas las tiendas', tiendas)}</select>
      </label>
      <label class="f-premio"><span>Premio</span>
        <select data-filtro="premio">${opciones('Todos los premios', premios)}</select>
      </label>
      <button type="button" class="limpiar" data-limpiar hidden>Limpiar filtros</button>
      <button type="button" class="imprimir" data-imprimir>Guardar como PDF</button>
    </div>
    <p class="filtros-aviso" data-aviso hidden>Los indicadores, el gráfico y las tablas de arriba
    también responden a estos filtros.</p>
  </div>`;
}

function figura(titulo: string, path: string | null, fotos: FotosEmbebidas, pie: string): string {
  if (path === null || path.trim() === '') return '';
  const dataUri = fotos.get(path);
  const cuerpo = dataUri === undefined
    // Se dice que falta y por qué puede faltar. Antes decía solo "No se pudo incluir esta foto" y
    // quien recibía el archivo no sabía si el promotor no la tomó o si falló la descarga.
    ? '<p class="foto-falta">No se pudo incluir esta foto al generar el reporte. '
      + 'El registro sí la tiene.</p>'
    : `<button type="button" data-lupa="${escaparHtml(dataUri)}" data-pie="${escaparHtml(pie)}">`
      + `<img src="${escaparHtml(dataUri)}" alt="${escaparHtml(titulo)}" loading="lazy"></button>`;
  return `<figure><figcaption>${escaparHtml(titulo)}</figcaption>${cuerpo}</figure>`;
}

function campo(etiqueta: string, valor: string): string {
  // La regla del diseño: un campo sin dato NO se imprime. El reporte anterior pintaba "—" en cinco
  // campos por tarjeta y eso llenaba de ruido la evidencia sin decir nada.
  if (valor.trim() === '') return '';
  return `<div><dt>${escaparHtml(etiqueta)}</dt><dd>${escaparHtml(valor)}</dd></div>`;
}

interface ContextoTarjeta {
  fotos: FotosEmbebidas;
  conMonto: boolean;
  /** `false` cuando NINGÚN registro del reporte trae número de boleta: la campaña no lo pide. */
  conBoleta: boolean;
  mostrarFotos: boolean;
  mostrarObservaciones: boolean;
  variasCampanias: boolean;
}

function tarjeta(r: CanjeRow, ctx: ContextoTarjeta): string {
  const anulado = r.estado === 'anulado';
  const premios = premiosDelCanje(r);
  const dins = dinamicasDelCanje(r);
  const fechaTexto = fechaCorta(r.fecha);

  // El titular. El diseño encabeza con la boleta porque es lo que se audita, pero
  // `pide_numero_boleta` viene apagado por defecto: en una campaña así TODAS las tarjetas
  // quedarían sin titular. Por eso el respaldo está en la plantilla y no en el dato.
  const titular = ctx.conBoleta
    ? `<span class="rotulo-mini">Boleta</span><span class="boleta-num">${
      r.boletaNumero === null || r.boletaNumero.trim() === ''
        ? '<span class="boleta-sin">Sin número</span>'
        : escaparHtml(r.boletaNumero)}</span>`
    : `<span class="rotulo-mini">Canje</span><span class="boleta-num">${
      escaparHtml(`${fechaTexto} · ${r.pdv?.name ?? 'sin tienda'}`)}</span>`;

  // El chip ámbar es genérico para cualquier bandera que devuelva el servidor, no una lista
  // cerrada: así una bandera nueva aparece sin tocar esta plantilla, que es justo el error que
  // `banderasDeCanje` vino a cerrar.
  const chips = (anulado ? ['<span class="chip chip-rojo">Anulado</span>'] : [])
    .concat(banderasDeCanje(r).map((b) => `<span class="chip chip-ambar">${escaparHtml(b)}</span>`))
    .join('');

  const chipsPremio = premios.length === 0
    ? '<span class="premio-din">Sin premio registrado</span>'
    : premios.map((p) => `<span class="chip-premio">${escaparHtml(`${p.nombre} ×${p.unidades}`)}</span>`
      + (p.equivalencia ? '<span class="premio-din">por equivalencia</span>' : '')).join('');

  const recurso = r.worker === null
    ? ''
    : `${r.worker.firstNames} ${r.worker.lastNames} · ${r.worker.dni}`;

  const evidencia = !ctx.mostrarFotos ? '' : `<div class="evidencia">${
    figura('Boleta', r.fotoBoletaPath, ctx.fotos, `Boleta · ${fechaTexto}`)}${
    figura('Premio', r.fotoPremioPath, ctx.fotos, `Premio · ${fechaTexto}`)}</div>`;

  const obs = ctx.mostrarObservaciones && (r.observaciones ?? '').trim() !== ''
    ? `<div class="obs"><div class="obs-rotulo">Observaciones del promotor</div>`
      + `<p>${escaparHtml(r.observaciones)}</p></div>`
    : '';

  // La tarjeta solo lleva su `id`. Antes llevaba fecha, tienda y la lista de premios en atributos
  // para que el script filtrara leyendo el DOM, y la lista iba unida por un caracter de control:
  // eso es HTML invalido y Vite se negaba a parsear el archivo. El dato ya viaja estructurado en el
  // JSON incrustado, asi que repetirlo en el markup era duplicarlo y arriesgarse a que las dos
  // copias dijeran cosas distintas.
  return `<article class="canje${anulado ? ' anulado' : ''}" data-canje="${escaparHtml(r.id)}"${
  anulado ? ' data-anulado="1"' : ''}>
  <div>
    <div class="boleta-fila">${titular}${chips}</div>
    <div class="premio-fila">
      <span class="rotulo-mini">Entregado</span>${chipsPremio}${
  dins.length === 0 ? '' : `<span class="premio-din">${
    escaparHtml(dins.map((d) => d.rotulo).join(' · '))}</span>`}
    </div>
    <dl>
${campo('Fecha del canje', fechaTexto)}
${campo('Tienda', tiendaDe(r))}
${campo('Promotor', recurso)}
${campo('Registrado', formatInTimeZone(r.registradoEn, TZ, 'dd/MM/yyyy HH:mm'))}
${r.ocurridoEn === null
    ? ''
    : campo('Momento declarado', formatInTimeZone(r.ocurridoEn, TZ, 'dd/MM/yyyy HH:mm'))}
${campo('Canjes', String(r.totalCanjes))}
${campo('Cliente', r.clienteNombre ?? '')}
${campo('Contacto', r.clienteContacto ?? '')}
${ctx.conMonto ? campo('Monto', r.montoCompra === null ? '—' : soles(r.montoCompra)) : ''}
${ctx.variasCampanias ? campo('Campaña', r.campaignId) : ''}
    </dl>${obs}
  </div>${evidencia}
</article>`;
}

/** Orden de las tarjetas. `rows` NO se ordena en el sitio: es estado de React. */
function ordenarCanjes(rows: CanjeRow[], orden: OrdenCanjes): CanjeRow[] {
  const copia = [...rows];
  if (orden === 'antiguo') {
    return copia.sort((a, b) => a.fecha.localeCompare(b.fecha)
      || a.registradoEn.localeCompare(b.registradoEn) || a.id.localeCompare(b.id));
  }
  if (orden === 'boleta') {
    return copia.sort((a, b) => (a.boletaNumero ?? '').localeCompare(b.boletaNumero ?? '', 'es')
      || a.id.localeCompare(b.id));
  }
  // Por defecto: fecha descendente y, dentro del día, lo último registrado primero. El desempate
  // por `id` es lo que hace que dos descargas del mismo dato salgan idénticas.
  return copia.sort((a, b) => b.fecha.localeCompare(a.fecha)
    || b.registradoEn.localeCompare(a.registradoEn) || a.id.localeCompare(b.id));
}

function listaCanjes(rows: CanjeRow[], ctx: ContextoTarjeta, agrupar: boolean): string {
  if (rows.length === 0) {
    return '<p class="sin-resultados">No hay canjes con esos filtros.</p>';
  }
  if (!agrupar) return rows.map((r) => tarjeta(r, ctx)).join('\n');

  const salida: string[] = [];
  let dia = '';
  for (const r of rows) {
    if (r.fecha !== dia) {
      dia = r.fecha;
      const delDia = rows.filter((x) => x.fecha === dia && x.estado === 'registrado');
      const unidades = delDia.reduce((s, x) => s + unidadesDePremio(x), 0);
      salida.push(`<div class="dia" data-dia="${escaparHtml(dia)}">`
        + `<span class="dia-titulo">${escaparHtml(fechaCorta(dia))}</span>`
        + `<span class="dia-regla"></span>`
        + `<span class="dia-cuenta">${escaparHtml(
          `${plural(delDia.length, 'registro', 'registros')} · ${
            plural(unidades, 'unidad', 'unidades')}`,
        )}</span></div>`);
    }
    salida.push(tarjeta(r, ctx));
  }
  return salida.join('\n');
}

function pie(meta: ReporteMeta, generatedAt: Date): string {
  const izquierda = `${meta.campaignName} · ${meta.periodStart} a ${meta.periodEnd} · generado el ${
    formatInTimeZone(generatedAt, TZ, 'dd/MM/yyyy HH:mm')}`;
  return `<footer class="pie"><div class="limite">
  <div>${escaparHtml(izquierda)}</div>
  <div class="pie-marca">treid · información reservada</div>
</div></footer>`;
}


/* ------------------------------------------------------------------ *
 * El script del archivo
 *
 * Este archivo llevaba CERO JavaScript por decisión explícita: "un archivo de evidencia que se
 * abre fuera de la aplicación no tiene por qué ejecutar nada". El rediseño lo revierte a pedido
 * del jefe de operaciones (20-ago) porque los filtros que recalculan todo el reporte son el punto
 * del rediseño. La reversión viene con tres condiciones, y las tres están implementadas:
 *
 * 1. **El documento se genera COMPLETO y ya calculado.** El script no pinta el reporte: lo filtra.
 *    Con JavaScript apagado se ve todo el reporte, con sus cifras, igual que antes; solo no se
 *    puede filtrar. Nunca hay una pantalla vacía esperando que algo se ejecute.
 * 2. **El script no escribe HTML.** Todo lo que sale de los datos entra por `textContent`, así que
 *    no hay un segundo camino de escapado que pueda equivocarse. Lo único que se construye son
 *    nodos, con `createElement`.
 * 3. **Autocomprobación.** Al cargar, el script recalcula el estado SIN filtros y lo compara con
 *    las cifras que el servidor ya escribió en el documento. Si no coinciden, se apaga solo: quita
 *    los controles y deja el reporte tal como se generó. Es la barandilla contra el error de
 *    fondo —que el archivo muestre dos cifras distintas del mismo dato—, que ya ocurrió una vez
 *    con la pantalla, el Excel y este HTML dando tres números de "canjes".
 * ------------------------------------------------------------------ */

const SCRIPT = `
(function () {
  'use strict';
  var raiz = document.getElementById('datos-reporte');
  if (!raiz) return;
  var datos;
  try { datos = JSON.parse(raiz.textContent || '[]'); } catch (e) { return; }

  var barra = document.querySelector('[data-bloque="filtros"]');
  var selects = {
    fecha: document.querySelector('[data-filtro="fecha"]'),
    tienda: document.querySelector('[data-filtro="tienda"]'),
    premio: document.querySelector('[data-filtro="premio"]')
  };
  var botonLimpiar = document.querySelector('[data-limpiar]');
  var botonImprimir = document.querySelector('[data-imprimir]');
  var aviso = document.querySelector('[data-aviso]');
  var alcance = document.querySelector('[data-alcance]');
  var tarjetas = [].slice.call(document.querySelectorAll('[data-canje]'));
  var dias = [].slice.call(document.querySelectorAll('[data-dia]'));
  var sinResultados = document.querySelector('[data-sin-resultados]');

  // Los anulados vienen en la lista, marcados: su tarjeta responde a los filtros pero NUNCA entra
  // en un acumulado. Todo lo que suma sale de \`vigentes\`; \`porId\` solo decide que se ve.
  var vigentes = datos.filter(function (c) { return !c.anulado; });
  var totalRegistros = vigentes.length;
  var porId = {};
  for (var q = 0; q < datos.length; q++) porId[datos[q].id] = datos[q];

  // ---- Cálculo: las mismas fórmulas que el servidor, y la autocomprobación lo verifica ----

  function unidadesDe(c) {
    var s = 0;
    for (var i = 0; i < c.premios.length; i++) s += c.premios[i].unidades;
    return s;
  }

  function pasa(c, f) {
    if (!c) return false;
    if (f.fecha && c.fecha !== f.fecha) return false;
    if (f.tienda && c.tienda !== f.tienda) return false;
    if (f.premio) {
      var tiene = false;
      for (var i = 0; i < c.premios.length; i++) {
        if (c.premios[i].nombre === f.premio) { tiene = true; break; }
      }
      if (!tiene) return false;
    }
    return true;
  }

  function filtrar(f) {
    return vigentes.filter(function (c) {
      if (f.fecha && c.fecha !== f.fecha) return false;
      if (f.tienda && c.tienda !== f.tienda) return false;
      if (f.premio) {
        var tiene = false;
        for (var i = 0; i < c.premios.length; i++) {
          if (c.premios[i].nombre === f.premio) { tiene = true; break; }
        }
        if (!tiene) return false;
      }
      return true;
    });
  }

  function plural(n, uno, varios) { return n + ' ' + (n === 1 ? uno : varios); }

  var MESES = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  function fechaCorta(iso) {
    var m = /^(\\d{4})-(\\d{2})-(\\d{2})$/.exec(iso);
    if (!m) return iso;
    return Number(m[3]) + ' ' + (MESES[Number(m[2]) - 1] || m[2]) + ' ' + m[1];
  }

  function agregar(sel) {
    var canjes = 0, unidades = 0, monto = 0, conMonto = 0;
    var fechas = {}, tiendas = {}, premios = {};
    var porFecha = {}, porEntrega = {};
    for (var i = 0; i < sel.length; i++) {
      var c = sel[i];
      canjes += c.canjes;
      unidades += unidadesDe(c);
      if (c.monto !== null) { monto += c.monto; conMonto++; }
      fechas[c.fecha] = true;
      tiendas[c.tienda] = true;
      if (!porFecha[c.fecha]) porFecha[c.fecha] = { registros: 0, canjes: 0 };
      porFecha[c.fecha].registros++;
      porFecha[c.fecha].canjes += c.canjes;
      for (var j = 0; j < c.premios.length; j++) {
        var p = c.premios[j];
        if (!premios[p.nombre]) premios[p.nombre] = { unidades: 0, registros: 0 };
        premios[p.nombre].unidades += p.unidades;
        premios[p.nombre].registros++;
        var clave = c.tienda + '\\u001f' + c.fecha + '\\u001f' + p.nombre;
        if (!porEntrega[clave]) {
          porEntrega[clave] = { tienda: c.tienda, fecha: c.fecha, premio: p.nombre,
                                unidades: 0, canjes: 0 };
        }
        porEntrega[clave].unidades += p.unidades;
        porEntrega[clave].canjes += c.canjes;
      }
    }
    return {
      registros: sel.length, canjes: canjes, unidades: unidades,
      monto: monto, conMonto: conMonto,
      fechas: Object.keys(fechas).sort(),
      tiendas: Object.keys(tiendas).sort(),
      premios: premios, porFecha: porFecha, porEntrega: porEntrega
    };
  }

  // ---- Pintado. Nada de innerHTML con datos: solo textContent y createElement ----

  function texto(sel, valor) {
    var n = document.querySelector(sel);
    if (n) n.textContent = valor;
  }

  function pintarKpis(a) {
    texto('[data-kpi="registros"]', String(a.registros));
    texto('[data-kpi="canjes"]', String(a.canjes));
    texto('[data-kpi-detalle="canjes"]', plural(a.unidades, 'unidad', 'unidades') + ' entregadas');
    texto('[data-kpi="tiendas"]', String(a.tiendas.length));
    texto('[data-kpi-detalle="tiendas"]',
      a.tiendas.length === 1 ? a.tiendas[0] : 'con al menos un canje');
    texto('[data-kpi="dias"]', String(a.fechas.length));
    texto('[data-kpi-detalle="dias"]', a.fechas.length === 0 ? '\\u2014'
      : fechaCorta(a.fechas[0]) + ' \\u2013 ' + fechaCorta(a.fechas[a.fechas.length - 1]));
    if (document.querySelector('[data-kpi="monto"]')) {
      texto('[data-kpi="monto"]', 'S/ ' + a.monto.toFixed(2));
      texto('[data-kpi-detalle="monto"]', a.conMonto === 0
        ? 'Ning\\u00fan registro trae monto anotado'
        : 'Ticket promedio S/ ' + (a.monto / a.conMonto).toFixed(2) + ' \\u00b7 '
          + plural(a.conMonto, 'registro', 'registros'));
    }
  }

  function pintarGrafico(a) {
    var caja = document.querySelector('[data-barras]');
    if (!caja) return;
    var nombres = Object.keys(a.premios).sort(function (x, y) {
      return a.premios[y].unidades - a.premios[x].unidades || x.localeCompare(y, 'es');
    });
    var total = 0, max = 1;
    for (var i = 0; i < nombres.length; i++) {
      total += a.premios[nombres[i]].unidades;
      if (a.premios[nombres[i]].unidades > max) max = a.premios[nombres[i]].unidades;
    }
    caja.textContent = '';
    if (nombres.length === 0) {
      var p = document.createElement('p');
      p.className = 'sin-resultados';
      p.textContent = 'Ning\\u00fan premio con los filtros seleccionados.';
      caja.appendChild(p);
      return;
    }
    for (var k = 0; k < nombres.length; k++) {
      var d = a.premios[nombres[k]];
      var fila = document.createElement('div');
      fila.className = 'barra';
      var alto = document.createElement('div');
      alto.className = 'barra-alto';
      var nom = document.createElement('span');
      nom.className = 'barra-nombre';
      nom.textContent = nombres[k];
      var det = document.createElement('span');
      det.className = 'barra-detalle';
      det.textContent = plural(d.registros, 'registro', 'registros') + ' \\u00b7 '
        + (total === 0 ? 0 : Math.round((d.unidades / total) * 100)) + '% del total';
      alto.appendChild(nom); alto.appendChild(det);
      var fb = document.createElement('div');
      fb.className = 'barra-fila';
      var riel = document.createElement('div');
      riel.className = 'barra-riel';
      var relleno = document.createElement('div');
      relleno.className = 'barra-relleno';
      relleno.style.width = Math.round((d.unidades / max) * 100) + '%';
      riel.appendChild(relleno);
      var cifra = document.createElement('span');
      cifra.className = 'barra-cifra num';
      cifra.textContent = String(d.unidades);
      fb.appendChild(riel); fb.appendChild(cifra);
      fila.appendChild(alto); fila.appendChild(fb);
      caja.appendChild(fila);
    }
  }

  function celda(texto2, clase) {
    var td = document.createElement('td');
    if (clase) td.className = clase;
    td.textContent = texto2;
    return td;
  }

  function pintarTablas(a) {
    var cuerpo = document.querySelector('[data-cuerpo="entregas"]');
    if (cuerpo) {
      var claves = Object.keys(a.porEntrega).sort(function (x, y) {
        var ex = a.porEntrega[x], ey = a.porEntrega[y];
        return ex.tienda.localeCompare(ey.tienda, 'es')
          || ex.fecha.localeCompare(ey.fecha)
          || ex.premio.localeCompare(ey.premio, 'es');
      });
      cuerpo.textContent = '';
      if (claves.length === 0) {
        var tr0 = document.createElement('tr');
        var td0 = celda('Ninguna entrega con los filtros seleccionados.', 'vacio');
        td0.colSpan = 5;
        tr0.appendChild(td0);
        cuerpo.appendChild(tr0);
      }
      for (var i = 0; i < claves.length; i++) {
        var e = a.porEntrega[claves[i]];
        var tr = document.createElement('tr');
        tr.setAttribute('data-fila', 'entrega');
        tr.appendChild(celda(e.tienda, 'tienda'));
        tr.appendChild(celda(fechaCorta(e.fecha), ''));
        tr.appendChild(celda(e.premio, 'premio'));
        tr.appendChild(celda(String(e.unidades), 'der'));
        tr.appendChild(celda(String(e.canjes), 'der apoyo'));
        cuerpo.appendChild(tr);
      }
      var nPremios = Object.keys(a.premios).length;
      texto('[data-celda="dias"]', plural(a.fechas.length, 'd\\u00eda', 'd\\u00edas'));
      texto('[data-celda="premios"]', plural(nPremios, 'premio', 'premios'));
      texto('[data-celda="unidades"]', String(a.unidades));
      texto('[data-celda="canjes"]', String(a.canjes));
    }

    var cuerpoF = document.querySelector('[data-cuerpo="actividad"]');
    if (cuerpoF) {
      var fechas = a.fechas.slice();
      cuerpoF.textContent = '';
      if (fechas.length === 0) {
        var tr1 = document.createElement('tr');
        var td1 = celda('Ninguna fecha con los filtros seleccionados.', 'vacio');
        td1.colSpan = 4;
        tr1.appendChild(td1);
        cuerpoF.appendChild(tr1);
      }
      var corrido = 0;
      for (var j = 0; j < fechas.length; j++) {
        var f = a.porFecha[fechas[j]];
        corrido += f.canjes;
        var trf = document.createElement('tr');
        trf.setAttribute('data-fila', 'fecha');
        trf.appendChild(celda(fechaCorta(fechas[j]), ''));
        trf.appendChild(celda(String(f.registros), 'der'));
        trf.appendChild(celda(String(f.canjes), 'der'));
        trf.appendChild(celda(String(corrido), 'der apoyo'));
        cuerpoF.appendChild(trf);
      }
    }
  }

  function pintarTarjetas(f, a) {
    var visibles = 0;
    for (var i = 0; i < tarjetas.length; i++) {
      var t = tarjetas[i];
      // Por 'id' contra el JSON. Antes se leian atributos 'data-fecha'/'data-tienda'/'data-premios'
      // del propio nodo: la lista de premios iba unida por un caracter de control y eso es HTML
      // invalido. Con el id basta, y el dato queda en un solo sitio.
      var ok = pasa(porId[t.getAttribute('data-canje')], f);
      t.hidden = !ok;
      if (ok) visibles++;
    }
    // Los encabezados de día se esconden cuando no les queda ninguna tarjeta visible detrás.
    for (var k = 0; k < dias.length; k++) {
      var dia = dias[k].getAttribute('data-dia');
      dias[k].hidden = !!(f.fecha && f.fecha !== dia);
    }
    if (sinResultados) sinResultados.hidden = visibles > 0;
    texto('[data-cuenta-detalle]', plural(a.registros, 'registro', 'registros') + ' \\u00b7 '
      + plural(a.unidades, 'unidad', 'unidades') + ' entregadas');
  }

  function leerFiltros() {
    return {
      fecha: selects.fecha ? selects.fecha.value : '',
      tienda: selects.tienda ? selects.tienda.value : '',
      premio: selects.premio ? selects.premio.value : ''
    };
  }

  function aplicar() {
    var f = leerFiltros();
    var hay = !!(f.fecha || f.tienda || f.premio);
    var sel = filtrar(f);
    var a = agregar(sel);
    pintarKpis(a);
    pintarGrafico(a);
    pintarTablas(a);
    pintarTarjetas(f, a);
    if (botonLimpiar) botonLimpiar.hidden = !hay;
    if (aviso) aviso.hidden = !hay;
    if (alcance) {
      alcance.textContent = hay
        ? 'Con los filtros aplicados \\u00b7 ' + a.registros + ' de ' + totalRegistros + ' registros'
        : 'Toda la campa\\u00f1a \\u00b7 ' + plural(a.registros, 'registro vigente', 'registros vigentes');
    }
  }

  // ---- Autocomprobación: si el recálculo no reproduce lo generado, el script se apaga ----

  function apagar(motivo) {
    if (barra) barra.hidden = true;
    if (window.console && console.warn) {
      console.warn('Reporte de canjes: filtros desactivados (' + motivo + ').');
    }
  }

  var base = agregar(vigentes);
  var esperado = document.querySelector('[data-kpi="canjes"]');
  if (!esperado || String(base.canjes) !== (esperado.textContent || '').trim()) {
    // El documento ya trae todo pintado y calculado por el servidor, así que apagarse deja un
    // reporte correcto y completo — solo sin filtros. Es preferible a filtrar con una fórmula que
    // no reproduce lo que el archivo dice.
    apagar('el rec\\u00e1lculo no coincide con las cifras del documento');
  } else {
    for (var s in selects) {
      if (selects[s]) selects[s].addEventListener('change', aplicar);
    }
    if (botonLimpiar) {
      botonLimpiar.addEventListener('click', function () {
        for (var s2 in selects) { if (selects[s2]) selects[s2].value = ''; }
        aplicar();
      });
    }
  }

  if (botonImprimir) {
    botonImprimir.addEventListener('click', function () { window.print(); });
  }

  // ---- Lupa de evidencia ----

  var lupa = null;
  function cerrarLupa() {
    if (lupa && lupa.parentNode) lupa.parentNode.removeChild(lupa);
    lupa = null;
    document.removeEventListener('keydown', alTeclear);
  }
  function alTeclear(ev) { if (ev.key === 'Escape') cerrarLupa(); }

  document.addEventListener('click', function (ev) {
    var boton = ev.target && ev.target.closest ? ev.target.closest('[data-lupa]') : null;
    if (!boton) return;
    cerrarLupa();
    lupa = document.createElement('div');
    lupa.className = 'lupa';
    var img = document.createElement('img');
    img.src = boton.getAttribute('data-lupa');
    img.alt = '';
    var pie2 = document.createElement('div');
    pie2.className = 'lupa-pie';
    pie2.textContent = boton.getAttribute('data-pie') || '';
    var chico = document.createElement('small');
    chico.textContent = 'Clic o Esc para cerrar';
    pie2.appendChild(chico);
    lupa.appendChild(img);
    lupa.appendChild(pie2);
    lupa.addEventListener('click', cerrarLupa);
    document.body.appendChild(lupa);
    document.addEventListener('keydown', alTeclear);
  });
})();
`;


/* ------------------------------------------------------------------ *
 * Ensamblado
 * ------------------------------------------------------------------ */

export type OrdenCanjes = 'reciente' | 'antiguo' | 'boleta';

/** Las cuatro banderas de plantilla del handoff de diseño. Los valores por defecto son el reporte
 * que se venía entregando, así que un llamador que no las pase no nota el cambio. */
export interface OpcionesReporteCanjes {
  ordenCanjes?: OrdenCanjes;
  agruparPorFecha?: boolean;
  /** `false` omite la evidencia. Es la vía para un reporte liviano, sin las fotos incrustadas. */
  mostrarFotos?: boolean;
  mostrarObservaciones?: boolean;
}

/**
 * Error de uso: el reporte HTML es POR CAMPAÑA.
 *
 * La bandeja permite mirar «Todas las campañas», y con ese filtro el archivo saldría con canjes de
 * varias marcas dentro y tarjetas que no dicen a cuál pertenece cada una — en un documento que se
 * le manda a un cliente de marca. Eso no es un problema de maquetado: es entregarle a un cliente
 * la operación de otro. Se corta acá, en el generador, y no solo en el botón: cualquier llamador
 * nuevo choca con lo mismo.
 */
export class ReporteMulticampaniaError extends Error {
  constructor(public readonly campanias: number) {
    super('El reporte HTML se genera por campaña: elige una campaña antes de descargar.');
    this.name = 'ReporteMulticampaniaError';
  }
}

/**
 * Reporte de canjes como un HTML **autocontenido**: las fotos van dentro del archivo, en
 * `data:` URI.
 *
 * Es la forma limpia de mandar evidencia a alguien que NO tiene usuario del sistema. Se manda una
 * COPIA de las fotos elegidas: el archivo se abre sin internet y sin sesión, y no deja ninguna
 * puerta abierta contra el bucket — a diferencia de un enlace firmado, que es una llave viva a ese
 * objeto. Además queda claro qué salió: exactamente lo que está en el archivo.
 *
 * Desde el rediseño (20-ago) el archivo lleva un `<script>` con los filtros y la lupa. El
 * documento se genera COMPLETO y ya calculado, así que sin JavaScript se ve todo igual —solo no se
 * filtra—. Ver el comentario de `SCRIPT` para las tres condiciones de esa reversión.
 *
 * @throws ReporteMulticampaniaError si `rows` trae canjes de más de una campaña.
 */
export function buildCanjesHtml(
  meta: ReporteMeta, rows: CanjeRow[], fotos: FotosEmbebidas, generatedAt: Date = new Date(),
  opciones: OpcionesReporteCanjes = {},
): string {
  const campanias = new Set(rows.map((r) => r.campaignId));
  if (campanias.size > 1) throw new ReporteMulticampaniaError(campanias.size);

  const {
    ordenCanjes = 'reciente', agruparPorFecha = false,
    mostrarFotos = true, mostrarObservaciones = true,
  } = opciones;

  const vigentes = registrosVigentes(rows);
  const conMonto = capturaMonto(rows);
  // Si NINGÚN registro trae número de boleta, la campaña no lo pide (`pide_numero_boleta` viene
  // apagado por defecto) y la tarjeta se encabeza con fecha y tienda. Se decide una vez para todo
  // el reporte: tarjetas con dos titulares distintos se leerían como dos tipos de registro.
  const conBoleta = vigentes.some((r) => (r.boletaNumero ?? '').trim() !== '');
  const t = totalesGenerales(rows);
  const unidades = vigentes.reduce((s, r) => s + unidadesDePremio(r), 0);
  const titulo = `Reporte de canjes — ${meta.campaignName}`;

  const ctx: ContextoTarjeta = {
    fotos, conMonto, conBoleta, mostrarFotos, mostrarObservaciones,
    variasCampanias: false,
  };
  const ordenadas = ordenarCanjes(rows, ordenCanjes);

  const cuerpo = rows.length === 0
    ? `<section class="seccion"><p class="sin-resultados">No hay canjes con esos filtros.</p></section>`
    : `${bloqueIndicadores(rows, conMonto)}
${grafico(rows)}
${desglose(rows)}
<section class="seccion-kpi" data-bloque="detalle">
  <div class="seccion-cabeza">
    <h3>Detalle de canjes</h3>
    <div class="seccion-alcance" data-cuenta-detalle>${escaparHtml(
      `${plural(t.registros, 'registro', 'registros')} · ${
        plural(unidades, 'unidad', 'unidades')} entregadas`,
    )}</div>
  </div>
  ${barraFiltros(rows)}
  <div class="canjes">${listaCanjes(ordenadas, ctx, agruparPorFecha)}</div>
  <p class="sin-resultados" data-sin-resultados hidden>Ningún canje coincide con los filtros
  seleccionados.</p>
</section>`;

  return `<!doctype html>
<html lang="es-PE">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escaparHtml(titulo)}</title>
<style>${ESTILOS}</style>
</head>
<body>
${portada(meta, rows, fotos, generatedAt)}
<main class="limite">
${cuerpo}
</main>
${pie(meta, generatedAt)}
<script type="application/json" id="datos-reporte">${serializarDatos(rows)}</script>
<script>${SCRIPT}</script>
</body>
</html>`;
}

export function reporteCanjesHtmlFileName(meta: ReporteMeta): string {
  return `reporte-canjes-${slugify(meta.campaignName)}-`
    + `${meta.periodStart}-a-${meta.periodEnd}.html`;
}
